<template>
  <div 
    class="asset-card" 
    :class="{ 
      'is-inactive': !asset.is_active,
      'is-assigned': asset.is_assigned
    }"
    @mouseenter="hovering = true"
    @mouseleave="hovering = false"
  >
    <div class="card-glass-content">
      <!-- Active/Inactive Toggle -->
      <div class="card-toggle">
        <label class="switch">
          <input
            type="checkbox"
            :checked="asset.is_active"
            @change="handleToggle"
          />
          <span class="slider round"></span>
        </label>
      </div>

      <!-- Actions Menu Button -->
      <div 
        ref="actionsButton"
        class="assigned-indicator actions-menu-button" 
        v-if="asset.is_assigned && asset.is_active"
        @click.stop="toggleActionsMenu"
        title="Actions"
      >
        <i :class="showActionsMenu ? 'fas fa-times' : 'fas fa-bars'"></i>
      </div>

      <!-- Icon Section with View Button -->
      <div class="card-icon-section">
        <div class="icon-wrapper">
          <div class="play-button-wrapper" :class="{ 'show': hovering }">
            <button class="play-button" @click="$emit('view', asset)">
              <i class="fas fa-play"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="card-content-section card-bg">
        <div class="text-center">
          <p class="asset-name" :class="{ 'long-text': asset.asset_name.length > 25 }">
            {{ asset.asset_name }}
          </p>
          <p class="asset-date">
            {{ formatDate(asset.date_created) }}
          </p>
        </div>

        <!-- Assign Button or Assigned Badge -->
        <div class="assign-button">
          <button
            v-if="!asset.is_assigned"
            class="assign-btn"
            @click="$emit('assign', asset)"
          >
            <i class="fas fa-link"></i> Assign
          </button>
          <div v-else class="assigned-badge">
            <i class="fas fa-check"></i> Assigned
          </div>
        </div>
      </div>
    </div>

    <!-- Actions Popup Menu -->
    <Teleport to="body">
      <nav 
        class="custom-popup-window" 
        v-if="showActionsMenu && asset.is_assigned"
        :style="popupStyle"
        @click.stop
      >
        <legend>Actions</legend>
        <ul>
          <li>
            <button @click="handleAddCustomer">
              <i class="fas fa-user-plus me-2"></i>
              Send Workflow
            </button>
          </li>
          <li>
            <button @click="handleOnBehalfApplication">
              <i class="fas fa-user-friends me-2"></i>
              On Behalf Of Application
            </button>
          </li>
        </ul>
      </nav>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: "AssetCard",
  props: {
    asset: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      hovering: false,
      showActionsMenu: false,
      popupStyle: {},
    };
  },
  methods: {
    handleToggle(event) {
      const isActive = event.target.checked;
      this.$emit('toggle', { asset: this.asset, isActive });
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    },
    toggleActionsMenu() {
      this.showActionsMenu = !this.showActionsMenu;
      if (this.showActionsMenu) {
        this.$nextTick(() => {
          const button = this.$refs.actionsButton;
          if (button) {
            const rect = button.getBoundingClientRect();
            this.popupStyle = {
              position: 'fixed',
              top: `${rect.bottom + 5}px`,
              left: `${rect.left}px`,
              zIndex: 9999,
            };
          }
        });
      }
    },
    closeActionsMenu() {
      this.showActionsMenu = false;
    },
    handleAddCustomer() {
      this.closeActionsMenu();
      this.$emit('add-customer', this.asset);
    },
    handleOnBehalfApplication() {
      this.closeActionsMenu();
      this.$emit('on-behalf-application', this.asset);
    },
    handleClickOutside(event) {
      const popup = document.querySelector('.custom-popup-window');
      if (this.showActionsMenu && !this.$el.contains(event.target) && (!popup || !popup.contains(event.target))) {
        this.closeActionsMenu();
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  emits: ["view", "toggle", "assign", "add-customer", "on-behalf-application"],
};
</script>

<style scoped>
.asset-card {
  position: relative;
  width: 100%;
  height: 265px;
  border-radius: 24px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s ease;
  background: rgba(248, 249, 250, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(108, 117, 125, 0.15);
  box-shadow:
    0 4px 8px rgba(0, 0, 0, 0.03),
    0 6px 12px rgba(0, 0, 0, 0.04);
}

.asset-card:hover {
  box-shadow:
    0 6px 12px rgba(0, 0, 0, 0.05),
    0 10px 16px rgba(0, 0, 0, 0.06);
  border-color: rgba(108, 117, 125, 0.25);
  transform: translateY(-2px);
}

.asset-card.is-inactive {
  opacity: 0.7;
  border-color: rgba(220, 53, 69, 0.3);
}

.asset-card.is-assigned {
  border: 1px solid rgba(25, 135, 84, 0.4);
  box-shadow:
    0 4px 8px rgba(25, 135, 84, 0.08),
    0 8px 16px rgba(25, 135, 84, 0.06);
}

.asset-card.is-assigned:hover {
  border-color: rgba(25, 135, 84, 0.6);
}

.card-glass-content {
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 12px;
}

.card-toggle {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 6px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
}

input:checked + .slider {
  background-color: var(--primary-color);
}

input:checked + .slider:before {
  transform: translateX(16px);
}

.slider.round {
  border-radius: 20px;
}

.slider.round:before {
  border-radius: 50%;
}

.assigned-indicator {
  position: absolute;
  top: 12px;
  left: 12px;
  width: 28px;
  height: 28px;
  background: rgba(25, 135, 84, 0.15);
  border: 1px solid var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  cursor: pointer;
  transition: all 0.2s ease;
}

.assigned-indicator:hover {
  transform: scale(1.1);
  background: rgba(25, 135, 84, 0.25);
}

.assigned-indicator i {
  color: var(--primary-color);
  font-size: 1.0rem;
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(25, 135, 84, 0.7);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(25, 135, 84, 0);
  }
}

.card-icon-section {
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  background: rgba(200, 200, 200, 0.15);
  border-radius: 14px;
  padding: 16px;
}

.icon-wrapper {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  position: relative;
}

.asset-card:hover .icon-wrapper {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.4);
  transform: scale(1.05);
}

.play-button-wrapper {
  opacity: 0.5;
  transition: all 0.3s ease;
}

.play-button-wrapper.show {
  opacity: 1;
}

.play-button {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: var(--primary-color);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(var(--primary-color-rgb, 89, 255, 116), 0.4);
  transition: all 0.2s ease;
}

.play-button:hover {
  transform: scale(1.1);
  background: var(--primary-color);
  box-shadow: 0 5px 14px rgba(var(--primary-color-rgb, 89, 255, 116), 0.6);
  opacity: 0.9;
}

.play-button i {
  color: #000;
  font-size: 1rem;
  margin-left: 2px;
}

.card-content-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  background: white;
  padding: 16px 14px 14px 14px;
  margin: 0 -12px -12px -12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
  border-radius: 0 0 14px 14px;
}

.card-bg {
  background-image: url("@/assets/images/card-bg.svg");
  background-size: cover;
}

.asset-name {
  margin: 0 0 4px 0;
  color: #212529;
  font-size: 0.85rem;
  font-weight: 700;
  background: transparent;
  line-height: 1.25;
  word-break: break-word;
  hyphens: auto;
}

.asset-name.long-text {
  font-size: 0.75rem;
  line-height: 1.2;
}

.asset-date {
  margin: 0;
  color: #6c757d;
  font-size: 0.65rem;
  font-weight: 500;
  background: transparent;
}

.assign-button {
  width: 75%;
  margin-top: 4px;
}

.assign-btn {
  width: 100%;
  background: var(--primary-color);
  border: none;
  border-radius: 10px;
  padding: 6px 12px;
  font-size: 0.7rem;
  font-weight: 700;
  color: #212529;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.assign-btn:hover {
  background: var(--primary-color);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
  opacity: 0.9;
}

.assign-btn i {
  font-size: 0.65rem;
}

.assigned-badge {
  width: 100%;
  background: rgba(25, 135, 84, 0.1);
  border: 1px solid rgba(25, 135, 84, 0.3);
  border-radius: 10px;
  padding: 5px 10px;
  font-size: 0.65rem;
  font-weight: 700;
  color: #198754;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.assigned-badge i {
  font-size: 0.65rem;
}
.slide-up-fade-leave-active {
  transition: all 0.5s ease;
}

.slide-up-fade-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

@media (max-width: 768px) {
  .asset-card {
    height: 220px;
  }

  .icon-wrapper {
    width: 60px;
    height: 60px;
  }

  .play-button {
    width: 38px;
    height: 38px;
  }

  .play-button i {
    font-size: 0.85rem;
  }

  .asset-name {
    font-size: 0.8rem;
  }

  .asset-date {
    font-size: 0.65rem;
  }
}

/* Actions Menu Button */
.actions-menu-button {
  cursor: pointer;
}

.actions-menu-button i {
  font-size: 0.85rem;
  transition: transform 0.2s ease;
}

.actions-menu-button:hover i {
  transform: scale(1.1);
}
</style>

<!-- Non-scoped styles for teleported popup -->
<style>
.custom-popup-window {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.08);
  min-width: 170px;
  overflow: hidden;
  animation: popupFadeIn 0.2s ease;
}

@keyframes popupFadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.custom-popup-window legend {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  padding: 12px 16px 8px;
  margin: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.custom-popup-window ul {
  list-style: none;
  padding: 8px 0;
  margin: 0;
}

.custom-popup-window li {
  padding: 0;
  margin: 0;
}

.custom-popup-window li button {
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: none;
  text-align: left;
  font-size: 0.8rem;
  font-weight: 500;
  color: #212529;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
}

.custom-popup-window li button:hover {
  background: rgba(25, 135, 84, 0.08);
  color: #198754;
}

.custom-popup-window li button i {
  font-size: 0.85rem;
  color: #6c757d;
  transition: color 0.2s ease;
}

.custom-popup-window li button:hover i {
  color: #198754;
}

.custom-popup-window li button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  color: #6c757d;
}

.custom-popup-window li button:disabled:hover {
  background: transparent;
  color: #6c757d;
}

.custom-popup-window li button:disabled i {
  color: #adb5bd;
}

.custom-popup-window li button:disabled:hover i {
  color: #adb5bd;
}
</style>